import { RuntimeScopeQuery, StepRunScopeQuery } from '@/types/stepExecutions.ts';
import { getRuntimeScopeKeyParts, getStepRunScopeKeyParts } from '@/utils/stepRunScope.ts';

export const ORG_KEY = ['orgs'];

export const SUITE_KEYS = {
  root: (orgId?: string, projectId?: string, identity = '') =>
    ['automation-suites', identity, orgId ?? '', projectId ?? ''] as const,
  list: (orgId?: string, projectId?: string, identity = '') =>
    [...SUITE_KEYS.root(orgId, projectId, identity), 'list'] as const,
  target: (
    orgId: string | undefined,
    projectId: string | undefined,
    suiteId: string,
    identity = '',
  ) => [...SUITE_KEYS.root(orgId, projectId, identity), 'suite', suiteId] as const,
  detail: (
    orgId: string | undefined,
    projectId: string | undefined,
    suiteId: string,
    identity = '',
  ) => [...SUITE_KEYS.target(orgId, projectId, suiteId, identity), 'detail'] as const,
};

export const LLM_MODEL_OPTIONS_KEY = ['llm', 'model_options'];

export const PROJECTS_BY_ORG_KEY = [...ORG_KEY, 'projects_by_org'];

export const PROJECTS_KEY = [...ORG_KEY, 'projects'];

export const AUTOMATIONS_KEY = [...PROJECTS_KEY, 'automations'];

export const AUTOMATIONS_LIST_KEY = (projectId?: string) => [
  ...AUTOMATIONS_KEY,
  'list',
  projectId,
];

// export const AUTOMATIONS_STATS_KEY = [...AUTOMATIONS_KEY, 'stats'];

export const PROJECT_RUNS_KEY = [...PROJECTS_KEY, 'runs'];
export const PROJECT_HUMAN_DECISIONS_KEY = (projectId?: string) => [...PROJECTS_KEY, projectId || '', 'human_decisions'];

export const RUNS_STATS_KEY = [...PROJECTS_KEY, 'runs', 'stats'];

export const AUTOMATION_BASE_KEY = (automationId: string) => [...AUTOMATIONS_KEY, automationId];

export const AUTOMATION_KEY = (automationId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'automation',
];

export const AUTOMATION_CONTRACT_PUBLISH_LIFECYCLE_KEY = (automationId?: string, versionId?: string) => [
  ...AUTOMATION_BASE_KEY(automationId || ''),
  'contract_publish_lifecycle',
  versionId || '',
];

export const AUTOMATION_PORTAL_KEY = (automationId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'portal',
];

export const AUTOMATION_PORTAL_RUNS_KEY = (automationId: string) => [
  ...AUTOMATION_PORTAL_KEY(automationId),
  'runs',
];

export const AUTOMATION_RUNS_KEY = (automationId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'runs',
];

export const AUTOMATION_RUNS_WITH_DATASETS_KEY = (automationId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'runs_with_datasets',
];

export const AUTOMATION_RUN_KEY = (automationId: string, runId?: string) => [
  ...AUTOMATION_RUNS_KEY(automationId),
  runId || '',
];

export const RUN_LATEST_EXECUTION_KEY = (automationId: string, runId?: string) => [
  ...AUTOMATION_RUN_KEY(automationId, runId),
  'latest_execution',
];

export const RUN_HUMAN_DECISIONS_KEY = (automationId: string, runId?: string) => [
  ...AUTOMATION_RUN_KEY(automationId, runId),
  'human_decisions',
];

export const RUN_CHAT_MESSAGES_KEY = (automationId: string, runId?: string) => [
  ...AUTOMATION_RUN_KEY(automationId, runId),
  'chat_messages',
];

export const RUN_AUTOMATION_VERSION_KEY = (automationId: string, runId?: string) => [
  ...AUTOMATION_RUN_KEY(automationId, runId),
  'automation_version',
];

export const RUN_WITH_DATASETS_KEY = (automationId: string, runId?: string) => [
  ...AUTOMATION_RUN_KEY(automationId, runId),
  'with_datasets',
];

export const RUN_TEST_OUTCOME_KEY = (runId?: string) => ['run_test_outcome', runId || ''];

export const AUTOMATION_BUILDER_READINESS_KEY = (automationId: string, versionId?: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'builder_readiness',
  versionId || '',
];

export const AUTOMATION_VERSION_TEST_SUITE_KEY = (automationId?: string, versionId?: string) => [
  ...AUTOMATION_BASE_KEY(automationId || ''),
  'version_test_suite',
  versionId || '',
];

export const AUTOMATION_VERSION_TEST_CASE_RUNS_KEY = (
  automationId?: string,
  versionId?: string,
  caseId?: string,
) => [
  ...AUTOMATION_BASE_KEY(automationId || ''),
  'version_test_case_runs',
  versionId || '',
  caseId || '',
];

export const AUTOMATION_TEST_FIXTURES_KEY = (automationId?: string) => [
  ...AUTOMATION_BASE_KEY(automationId || ''),
  'test_fixtures',
];

export const AUTOMATION_TEST_FIXTURE_INPUTS_KEY = (automationId?: string, versionId?: string) => [
  ...AUTOMATION_BASE_KEY(automationId || ''),
  'test_fixture_inputs',
  versionId || '',
];

export const RUN_LLM_USAGE_KEY = (automationId: string, runId?: string) => [
  ...AUTOMATION_RUN_KEY(automationId, runId),
  'llm_usage',
];

export const RUN_AUTO_EXPAND_PLAN_KEY = (automationId: string, runId?: string) => [
  ...AUTOMATION_RUN_KEY(automationId, runId),
  'auto_expand_plan',
];

// export const RUN_QUERY_KEYS = (automationId: string, runId?: string) => [
//   AUTOMATION_RUNS_KEY(automationId),
//   AUTOMATION_RUNS_WITH_DATASETS_KEY(automationId),
//   AUTOMATION_RUN_KEY(automationId, runId),
//   RUN_WITH_DATASETS_KEY(automationId, runId),
// ];

//////////////////////////////////////////////////////////////////

export const STEP_RUNS_BASE_KEY = (automationId: string, runId?: string) => [
  ...AUTOMATION_RUN_KEY(automationId, runId),
  'step_runs',
];

export const LATEST_STEP_RUNS_BASE_KEY = (automationId: string, runId?: string) => [
  ...STEP_RUNS_BASE_KEY(automationId, runId),
  'latest',
];

export const ALL_STEP_RUNS_BASE_KEY = (automationId: string, runId?: string) => [
  ...STEP_RUNS_BASE_KEY(automationId, runId),
  'all',
];

export const RETRY_PRESENTATION_BASE_KEY = (automationId: string, runId?: string) => [
  ...STEP_RUNS_BASE_KEY(automationId, runId),
  'retry_presentation',
];

export const RETRY_PRESENTATION_KEY = (automationId: string, runId?: string, stepRunId?: string) => [
  ...RETRY_PRESENTATION_BASE_KEY(automationId, runId),
  stepRunId || '',
];

export const WAITING_STEP_DEPENDENCIES_BASE_KEY = (automationId: string, runId?: string) => [
  ...STEP_RUNS_BASE_KEY(automationId, runId),
  'waiting_dependencies',
];

export const LATEST_STEP_RUNS_KEY = (
  automationId: string,
  runId?: string,
  scope?: StepRunScopeQuery,
) => [...LATEST_STEP_RUNS_BASE_KEY(automationId, runId), ...getStepRunScopeKeyParts(scope)];

export const ALL_STEP_RUNS_KEY = (
  automationId: string,
  runId?: string,
  scope?: StepRunScopeQuery,
) => [...ALL_STEP_RUNS_BASE_KEY(automationId, runId), ...getStepRunScopeKeyParts(scope)];

export const WAITING_STEP_DEPENDENCIES_KEY = (
  automationId: string,
  runId?: string,
  scope?: RuntimeScopeQuery,
) => [...WAITING_STEP_DEPENDENCIES_BASE_KEY(automationId, runId), ...getRuntimeScopeKeyParts(scope)];

export const STEP_EXECUTIONS_BASE_KEY = (automationId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'step_executions',
];

export const STEP_EXECUTION_KEY = (automationId: string, stepExecutionId?: string) => [
  ...STEP_EXECUTIONS_BASE_KEY(automationId),
  stepExecutionId || '',
];

export const STEP_EXECUTION_LOGS_KEY = (automationId: string, stepExecutionId?: string) => [
  ...STEP_EXECUTION_KEY(automationId, stepExecutionId),
  'logs',
];

export const STEP_EXECUTION_METADATA_KEY = (automationId: string, stepExecutionId?: string) => [
  ...STEP_EXECUTION_KEY(automationId, stepExecutionId),
  'metadata',
];

export const STEP_RUN_LLM_USAGE_KEY = (automationId: string, stepRunId?: string) => [
  ...STEP_RUNS_BASE_KEY(automationId),
  stepRunId || '',
  'llm_usage',
];

export const STEP_RUN_CONNECTIONS_KEY = (
  automationId: string,
  runId?: string,
  stepRunId?: string,
) => [...STEP_RUNS_BASE_KEY(automationId, runId), stepRunId || '', 'connections'];

export const STEP_RUN_LOG_PRESENTATION_KEY = (
  automationId: string,
  runId?: string,
  stepRunId?: string,
) => [...STEP_RUNS_BASE_KEY(automationId, runId), stepRunId || '', 'log_presentation'];

export const RUN_STEP_DEBUG_PREVIEW_KEY = (
  automationId: string,
  runId?: string,
  stepId?: string,
  fullPath?: string | null,
  stepRunId?: string,
) => [
  ...AUTOMATION_RUN_KEY(automationId, runId),
  'step_debug_preview',
  stepId || '',
  fullPath ?? '',
  stepRunId || '',
];

export const STEP_RUN_HISTORY_KEY = (
  automationId: string,
  runId?: string,
  stepId?: string,
  fullPath?: string | null,
  stepRunId?: string,
) => [...AUTOMATION_RUN_KEY(automationId, runId), 'step_run_history', stepId || '', fullPath ?? '', stepRunId || ''];

export const STEP_EXECUTION_EXTRACT_ENTITIES_KEY = (
  automationId: string,
  stepExecutionId?: string,
) => [...STEP_EXECUTION_KEY(automationId, stepExecutionId), 'extract_entities'];

//////////////////////////////////////////////////////////////////

export const EXTRACT_ENTITIES_BASE_KEY = (automationId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'extract_entities',
];

export const EXTRACT_ENTITY_BASE_KEY = (automationId: string, extractEntityId?: string) => [
  ...EXTRACT_ENTITIES_BASE_KEY(automationId),
  extractEntityId,
];

export const EXTRACT_ENTITY_ERROR_LOGS_KEY = (automationId: string, extractEntityId?: string) => [
  ...EXTRACT_ENTITY_BASE_KEY(automationId, extractEntityId),
  'logs',
];

export const EXTRACT_ENTITY_FIELDS_KEY = (automationId: string, extractEntityId?: string) => [
  ...EXTRACT_ENTITY_BASE_KEY(automationId, extractEntityId),
  'fields',
];

//////////////////////////////////////////////////////////////////

export const AUTOMATION_RUN_STATS_KEY = (automationId: string) => [
  ...AUTOMATION_RUNS_KEY(automationId),
  'stats',
];

export const AUTOMATION_LATEST_VERSION_KEY = (automationId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'latest_version',
];

export const AUTOMATION_LLM_USAGE_KEY = (automationId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'llm_usage',
];

export const AUTOMATION_VERSIONS_KEY = (automationId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'versions',
];

export const AUTOMATION_VERSION_KEY = (automationId: string, versionId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'version',
  versionId,
];

export const AUTOMATION_VERSION_DOCUMENTATION_KEY = (automationId: string, versionId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'version_documentation',
  versionId,
];

export const AUTOMATION_VERSION_DOCUMENTATION_EVENT_KEY = (
  automationId: string, versionId: string, eventId: string,
) => [...AUTOMATION_VERSION_DOCUMENTATION_KEY(automationId, versionId), 'event', eventId];

export const AUTOMATION_VERSION_LOCAL_TEST_EVIDENCE_KEY = (
  automationId: string,
  versionId: string,
) => [...AUTOMATION_VERSION_KEY(automationId, versionId), 'local-test-evidence'];

export const AUTOMATION_VERSION_LOCAL_TEST_RECEIPT_KEY = (
  automationId: string,
  versionId: string,
  requestId: string,
) => [...AUTOMATION_VERSION_LOCAL_TEST_EVIDENCE_KEY(automationId, versionId), 'receipt', requestId];

export const INTEGRATION_CONFIG_STEP_BASE_KEY = (automationId: string, stepId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'step',
  stepId,
];

export const INTEGRATION_CONFIG_STEP_ACTION_KEY = (automationId: string, stepId: string) => [
  ...INTEGRATION_CONFIG_STEP_BASE_KEY(automationId, stepId),
  'action',
];

export const INTEGRATION_CONFIG_SELECT_FIELD_OPTIONS_KEY = (
  automationId: string,
  stepId: string,
  fieldId: string,
) => [
  ...INTEGRATION_CONFIG_STEP_BASE_KEY(automationId, stepId),
  'field',
  fieldId,
  'select_field_options',
];

export const INTEGRATION_CONFIG_FIELD_SENSITIVE_VALUE_KEY = (
  automationId: string,
  stepId: string,
  fieldId: string,
) => [
  ...INTEGRATION_CONFIG_STEP_BASE_KEY(automationId, stepId),
  'field',
  fieldId,
  'webhook_api_key',
];

export const INTEGRATION_CONFIG_EMAIL_ROUTES_KEY = (automationId: string, stepId: string) => [
  ...INTEGRATION_CONFIG_STEP_BASE_KEY(automationId, stepId),
  'email_routes',
];

export const AUTOMATION_EMAIL_IDENTITY_KEY = (automationId: string) => [
  ...AUTOMATION_BASE_KEY(automationId),
  'email_identity',
];

export const INTEGRATIONS_KEY = [...PROJECTS_KEY, 'integrations'];

export const INTEGRATIONS_LIST_KEY = (projectId?: string) => [
  ...INTEGRATIONS_KEY,
  'list',
  projectId,
];

export const INTEGRATION_FILES_KEY = (integrationId?: string, filePath?: string) => [
  ...INTEGRATIONS_KEY,
  integrationId,
  'files',
  filePath,
];

export const STATIC_DATASETS_KEY = (automationId: string) => [
  ...PROJECTS_KEY,
  automationId,
  'static_datasets',
];

export const AUTOMATION_TRIGGER_DATASETS_KEY = (automationId?: string) => [
  ...PROJECTS_KEY,
  automationId,
  'trigger_datasets',
];

export const LINKED_DATASETS_KEY = (automationId: string) => [
  ...PROJECTS_KEY,
  automationId,
  'linked_dataset',
];

// export const LINKED_DATASET_KEY = [...PROJECTS_KEY, 'linked_datasets'];

// export const LINKED_DATASET_LATEST_DATASET_KEY = (linkedDatasetId?: string) => [
//   ...LINKED_DATASET_KEY,
//   linkedDatasetId,
//   'latest_dataset',
// ];

export const DATASETS_KEY = [...PROJECTS_KEY, 'datasets'];

export const DATASET_BASE_KEY = (datasetId?: string) => [...DATASETS_KEY, datasetId];

export const DATASET_KEY = (datasetId: string) => [...DATASET_BASE_KEY(datasetId)];

export const DATASET_NOTES_KEY = (datasetId?: string) => [...DATASET_BASE_KEY(datasetId), 'notes'];

export const DATASET_EXTRACT_EXECUTION_KEY = (datasetId?: string) => [
  ...DATASET_BASE_KEY(datasetId),
  'extract_execution',
];

export const DATASET_PARENT_KEY = (datasetId?: string) => [
  ...DATASET_BASE_KEY(datasetId),
  'parent',
];

export const DATASET_RELEVANT_STEPS_KEY = (datasetId?: string) => [
  ...DATASET_BASE_KEY(datasetId),
  'relevant_steps',
];

export const DATASET_CHILD_DATASETS_KEY = (datasetId?: string) => [
  ...DATASET_BASE_KEY(datasetId),
  'child_datasets',
];

export const DATASET_URL_KEY = (datasetId?: string) => [...DATASET_BASE_KEY(datasetId), 'URL'];

export const UPSTREAM_DATA_OUTPUTS_KEY = (automationId: string, versionId: string) => [
  ...AUTOMATION_VERSION_KEY(automationId, versionId),
  'upstream_data_outputs',
];

export const CHILDREN_DATA_OUTPUTS_KEY = (automationId: string, versionId: string) => [
  ...AUTOMATION_VERSION_KEY(automationId, versionId),
  'children_data_outputs',
];

export const OUTPUT_CONTRACTS_KEY = (automationId: string, versionId: string) => [
  ...AUTOMATION_VERSION_KEY(automationId, versionId),
  'output_contracts',
];

export const STATIC_DATASET_USAGE_KEY = (automationId: string) => [
  ...PROJECTS_KEY,
  automationId,
  'static_dataset_usage',
];
